/**
 * Import Validator
 * Reads a populated import template, validates data against metadata/lookups/FK values,
 * checks for existing records (duplicates/updates), verifies cross-tab dependencies,
 * and produces an annotated Excel file with errors and warnings highlighted.
 */
import ExcelJS from 'exceljs';
import { appCfgApi, dynamicApi } from '../services/api';
import { TABLE_CONFIG } from './importTemplateBuilder';

// Fields that are never required (system-managed or optional by nature)
const NEVER_REQUIRED = ['ID', 'Rev_Date', 'Rev_User', 'Comment', 'Serial_No']; // eslint-disable-line no-unused-vars

// Fields to exclude entirely from validation. ID and Register are system fields.
// SQL computed columns are auto-excluded elsewhere via the is_Computed flag on
// field metadata (populated by /api/appcfg/sync from sys.computed_columns).
const EXCLUDE_FIELDS = ['ID', 'Register'];

// Fields that skip FK/lookup validation entirely (true free-text with no reference table)
const NO_VALIDATION = [];

// Fields excluded from row comparison (system-managed). SQL computed columns
// are auto-excluded via the is_Computed flag on field metadata.
const COMPARE_EXCLUDE = ['ID', 'Rev_Date', 'Rev_User', 'Register'];

// FK tables and lookup categories that are views/aliases — map to the actual import source table + field
// e.g., Parent_Unique and Panel_Unique are lookup categories derived from the Panels table
const IMPORT_SOURCE_MAP = {
  'Parent_Unique': { table: 'Panels', field: 'Parent' },
  'Panel_Unique':  { table: 'Panels', field: 'Panel' },
  'Tag_Unique':    { table: 'Tags', field: 'Tag_ID' },
  'DTC':           { table: 'Panels', field: 'Parent' },
};

// Per-table rules: match keys for duplicate detection + dependency checks
const TAB_RULES = {
  'Source':         { matchFields: ['Project_Number'] },
  'Drawing':        { matchFields: ['Drawing_Name'] },
  'Drawing_Log':    { matchFields: ['Drawing_Name', 'Drawing_Rev'],
                      dependsOn: { table: 'Drawing', field: 'Drawing_Name', importField: 'Drawing_Name' } },
  'Panels':         { matchFields: ['Panel'] },
  'Tags':           { matchFields: ['Tag_ID'] },
  'IP_Address_SLN': { matchFields: ['IP_Address'] },
  'IP_Address_DLN': { matchFields: ['IP_Address'] },
  'Networks_ENET':  { matchFields: ['Parent', 'Switch', 'Switch_Number', 'Main_Expansion', 'Switch_Port'],
                      dependsOn: { table: 'Panels', fields: ['Panel', 'Parent'], importField: 'Parent' } },
  'Networks_Serial': { matchFields: ['Parent', 'Device'],
                      dependsOn: { table: 'Panels', fields: ['Panel', 'Parent'], importField: 'Parent' } },
  'Pwr_Dist':       { matchFields: ['Panel_Fed', 'Tag_Fed', 'Load_Fed'],
                      dependsOn: { table: 'Panels', field: 'Panel', importField: 'Panel' } },
};

// Theme for error highlighting
const ERROR_FILL = {
  type: 'pattern', pattern: 'solid',
  fgColor: { argb: 'FFFCE4EC' }  // Light red
};

const WARNING_FILL = {
  type: 'pattern', pattern: 'solid',
  fgColor: { argb: 'FFFFF3E0' }  // Light amber/orange
};

const SKIP_FILL = {
  type: 'pattern', pattern: 'solid',
  fgColor: { argb: 'FFF5F5F5' }  // Light gray
};

const SUMMARY_HEADER_FILL = {
  type: 'pattern', pattern: 'solid',
  fgColor: { argb: 'FFC62828' }
};

const INSERT_FILL = {
  type: 'pattern', pattern: 'solid',
  fgColor: { argb: 'FFE8F5E9' }  // Light green
};

const RECORD_HEADER_FILL = {
  type: 'pattern', pattern: 'solid',
  fgColor: { argb: 'FF1A5568' }  // WM teal
};

/**
 * Helper: get a value from a row object using case-insensitive key matching.
 */
function getRowValue(row, fieldName) {
  if (row[fieldName] !== undefined) return row[fieldName];
  const key = Object.keys(row).find(k => k.toLowerCase() === fieldName.toLowerCase());
  return key ? row[key] : undefined;
}

/**
 * Unwrap an ExcelJS cell.value into a primitive. Formula cells come back as
 * { formula, result }, shared formulas as { sharedFormula, result }, rich text
 * as { richText: [...] }, hyperlinks as { text, hyperlink }, and error cells
 * as { error: '#REF!' }. Without unwrapping, String(obj) yields "[object Object]"
 * and gets silently written to the DB for non-validated text columns.
 *
 * Returns { value, error } — value is the primitive (or null), error is a
 * short reason string if the cell contains an unresolvable formula/error.
 */
function unwrapCellValue(raw) {
  if (raw == null) return { value: raw, error: null };
  if (typeof raw !== 'object') return { value: raw, error: null };
  if (raw instanceof Date) return { value: raw, error: null };

  // Error cell (e.g. { error: '#DIV/0!' })
  if (raw.error && !raw.formula && !raw.sharedFormula) {
    return { value: null, error: `Cell error: ${raw.error}` };
  }

  // Formula / shared formula — recurse into result
  if (raw.formula !== undefined || raw.sharedFormula !== undefined) {
    if (raw.result === undefined || raw.result === null) {
      return { value: null, error: 'Formula has no cached result — open and save the file in Excel to force calculation' };
    }
    // result may itself be an error object like { error: '#N/A' }
    if (typeof raw.result === 'object' && raw.result.error) {
      return { value: null, error: `Formula error: ${raw.result.error}` };
    }
    return unwrapCellValue(raw.result);
  }

  // Rich text: { richText: [{ text, font }, ...] }
  if (Array.isArray(raw.richText)) {
    return { value: raw.richText.map(r => r.text || '').join(''), error: null };
  }

  // Hyperlink: { text, hyperlink }
  if (raw.text !== undefined) return { value: raw.text, error: null };

  // Unknown object shape — refuse to stringify, treat as empty with an error
  return { value: null, error: 'Unsupported cell value type' };
}

/**
 * Helper: build a composite match key string from field values (lowercase).
 */
function buildMatchKey(values) {
  return values.map(v => String(v ?? '').trim().toLowerCase()).join('||');
}

/**
 * Main entry point: validates a populated import file.
 *
 * @param {File} file - The uploaded .xlsx file
 * @param {Function} onProgress - Called with { step, total, message }
 * @returns {Object} { totalErrors, totalWarnings, errorsByTab, warningsByTab, annotatedBuffer }
 */
export async function validateImportFile({ file, onProgress }) {
  const totalSteps = 7;
  let currentStep = 0;
  const progress = (message) => {
    currentStep++;
    onProgress?.({ step: currentStep, total: totalSteps, message });
  };

  // Step 1: Read the uploaded file
  progress('Reading file...');
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  // Step 2: Fetch metadata (needed for column structure check)
  progress('Fetching field metadata...');
  const metadataMap = {};
  const metaResults = await Promise.allSettled(
    TABLE_CONFIG.map(async (t) => {
      const res = await appCfgApi.getFieldMetadata(t.dbTable);
      return { dbTable: t.dbTable, data: res.data };
    })
  );
  for (const result of metaResults) {
    if (result.status === 'fulfilled') {
      metadataMap[result.value.dbTable] = result.value.data;
    }
  }

  // Step 3: Validate column structure (names and order)
  progress('Validating column structure...');
  const structureErrors = {};
  let totalStructureErrors = 0;

  for (const tableConfig of TABLE_CONFIG) {
    const ws = workbook.getWorksheet(tableConfig.tabName);
    if (!ws) continue;

    const fields = metadataMap[tableConfig.dbTable];
    if (!fields) continue;

    // Expected columns: ID first, then all other fields in metadata order (matches template builder)
    const nonIdFields = fields.filter(f => f.field_Name !== 'ID');
    const expectedFields = [{ field_Name: 'ID', display_Label: 'ID' }, ...nonIdFields];
    const headerRow = ws.getRow(1);
    const tabErrors = [];

    // Read actual headers from the sheet
    const actualHeaders = [];
    headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      actualHeaders.push({ col: colNumber, value: String(cell.value || '').trim() });
    });

    // Check count mismatch
    if (actualHeaders.length !== expectedFields.length) {
      tabErrors.push({
        row: 1, col: 1, field: 'Header Row',
        value: `${actualHeaders.length} columns`,
        error: `Expected ${expectedFields.length} columns but found ${actualHeaders.length}`
      });
    }

    // Check each expected column in order
    for (let i = 0; i < expectedFields.length; i++) {
      const expected = expectedFields[i];
      const expectedLabel = expected.display_Label || expected.field_Name.replace(/_/g, ' ');
      const actual = actualHeaders[i];

      if (!actual) {
        tabErrors.push({
          row: 1, col: i + 1, field: expected.field_Name,
          value: '(missing)',
          error: `Column ${i + 1} missing — expected "${expectedLabel}"`
        });
      } else {
        const actualValue = actual.value;
        // Match against display_Label, field_Name, or derived label
        const isMatch =
          actualValue === expectedLabel ||
          actualValue === expected.field_Name ||
          actualValue === expected.field_Name.replace(/_/g, ' ');

        if (!isMatch) {
          tabErrors.push({
            row: 1, col: actual.col, field: expected.field_Name,
            value: actualValue,
            error: `Column ${i + 1} is "${actualValue}" — expected "${expectedLabel}"`
          });
        }
      }
    }

    if (tabErrors.length > 0) {
      structureErrors[tableConfig.tabName] = tabErrors;
      totalStructureErrors += tabErrors.length;
    }
  }

  // If structure errors exist, bail out early — no point validating data with wrong columns
  if (totalStructureErrors > 0) {
    // Build annotated workbook with just structure errors
    for (const [tabName, errors] of Object.entries(structureErrors)) {
      const ws = workbook.getWorksheet(tabName);
      if (!ws) continue;
      for (const err of errors) {
        const cell = ws.getCell(err.row, err.col);
        cell.fill = ERROR_FILL;
        cell.note = err.error;
      }
    }

    // Add summary sheet
    const existingSummary = workbook.getWorksheet('Validation');
    if (existingSummary) workbook.removeWorksheet(existingSummary.id);
    const summary = workbook.addWorksheet('Validation', {
      properties: { tabColor: { argb: 'FFC62828' } }
    });
    summary.orderNo = 0;
    summary.columns = [
      { width: 5 }, { width: 25 }, { width: 10 }, { width: 25 }, { width: 30 }, { width: 50 }
    ];
    summary.mergeCells('B2:F2');
    const titleCell = summary.getCell('B2');
    titleCell.value = `Column Structure Invalid — ${totalStructureErrors} error${totalStructureErrors !== 1 ? 's' : ''} (fix columns before data validation can proceed)`;
    titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FFC62828' } };
    summary.getCell('B3').value = `Validated: ${new Date().toLocaleString()}`;
    summary.getCell('B3').font = { name: 'Calibri', size: 11, color: { argb: 'FF64748B' } };

    let sRow = 5;
    const headers = ['Tab', 'Col #', 'Expected Field', 'Found', 'Error'];
    const headerCols = [2, 3, 4, 5, 6];
    headers.forEach((h, i) => {
      const cell = summary.getCell(sRow, headerCols[i]);
      cell.value = h;
      cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = SUMMARY_HEADER_FILL;
    });
    sRow++;

    for (const [tabName, errors] of Object.entries(structureErrors)) {
      for (const err of errors) {
        summary.getCell(sRow, 2).value = tabName;
        summary.getCell(sRow, 3).value = err.col;
        summary.getCell(sRow, 4).value = err.field;
        summary.getCell(sRow, 5).value = err.value;
        summary.getCell(sRow, 6).value = err.error;
        for (let c = 2; c <= 6; c++) {
          summary.getCell(sRow, c).font = { name: 'Calibri', size: 11 };
        }
        sRow++;
      }
    }

    const annotatedBuffer = await workbook.xlsx.writeBuffer();
    return {
      totalErrors: totalStructureErrors,
      totalWarnings: 0,
      errorsByTab: structureErrors,
      warningsByTab: {},
      annotatedBuffer,
      structureFailure: true
    };
  }

  // Step 4: Fetch lookups and FK data
  progress('Fetching lookup and FK data...');

  let lookupMap = {};
  try {
    const lookupsRes = await appCfgApi.getAllLookups();
    lookupMap = lookupsRes.data;
  } catch (e) { /* continue without lookups */ }

  const fkTableSet = new Set();
  const fkDisplayFieldMap = {};
  for (const [, fields] of Object.entries(metadataMap)) {
    for (const field of fields) {
      if (field.fK_Table && field.fK_Display_Field) {
        fkTableSet.add(field.fK_Table);
        fkDisplayFieldMap[field.fK_Table] = field.fK_Display_Field;
      }
    }
  }

  const fkValuesMap = {};
  const fkIdToDisplayMap = {}; // fkTable -> Map<id, displayValue> (for resolving FK IDs in record comparison)
  if (fkTableSet.size > 0) {
    const fkResults = await Promise.allSettled(
      [...fkTableSet].map(async (fkTable) => {
        const res = await dynamicApi.getAll(fkTable);
        const displayField = fkDisplayFieldMap[fkTable];
        const idMap = new Map();
        const values = res.data
          .map(row => {
            const val = getRowValue(row, displayField);
            const id = getRowValue(row, 'ID') ?? getRowValue(row, 'id');
            if (id != null && val != null) {
              idMap.set(String(id), String(val));
            }
            return val != null ? val : null;
          })
          .filter(Boolean)
          .map(v => String(v));
        return { fkTable, values, idMap };
      })
    );
    for (const result of fkResults) {
      if (result.status === 'fulfilled') {
        fkValuesMap[result.value.fkTable] = new Set(result.value.values.map(v => v.toLowerCase()));
        fkIdToDisplayMap[result.value.fkTable] = result.value.idMap;
      }
    }
  }

  // Build a per-field FK resolution helper: "tableName.fieldName" -> fkTable
  // Composite key prevents cross-table field-name pollution — e.g., Pwr_Dist.Panel is an
  // FK to Panels, but Tags.Panel is plain text (Panel_Unique lookup). Keying by field name
  // alone would apply Pwr_Dist's FK resolution to every table with a "Panel" column.
  const fieldFkMap = {}; // "tableName.fieldName" -> fkTable
  for (const [tableName, fields] of Object.entries(metadataMap)) {
    for (const field of fields) {
      if (field.fK_Table && field.fK_Display_Field) {
        fieldFkMap[tableName + '.' + field.field_Name] = field.fK_Table;
      }
    }
  }

  const lookupSets = {};
  for (const [category, values] of Object.entries(lookupMap)) {
    lookupSets[category] = new Set(values.map(v => String(v).toLowerCase()));
  }

  // Step 5: Fetch existing records for duplicate/update detection
  progress('Fetching existing records...');

  // Collect all tables we need: TABLE_CONFIG tables + dependency tables
  const tablesToFetch = new Set(TABLE_CONFIG.map(t => t.dbTable));
  for (const [, rules] of Object.entries(TAB_RULES)) {
    if (rules.dependsOn) {
      tablesToFetch.add(rules.dependsOn.table);
    }
  }

  const existingDataMap = {}; // dbTable -> array of row objects
  const existingResults = await Promise.allSettled(
    [...tablesToFetch].map(async (dbTable) => {
      const res = await dynamicApi.getAll(dbTable);
      return { dbTable, data: res.data };
    })
  );
  for (const result of existingResults) {
    if (result.status === 'fulfilled') {
      existingDataMap[result.value.dbTable] = result.value.data;
    }
  }

  // Pre-build match indexes for each table (for fast lookup)
  const matchIndexes = {}; // dbTable -> Map<matchKey, rowObject>
  for (const tableConfig of TABLE_CONFIG) {
    const rules = TAB_RULES[tableConfig.dbTable];
    if (!rules) continue;

    const existingRows = existingDataMap[tableConfig.dbTable] || [];
    const index = new Map();

    for (const row of existingRows) {
      const keyValues = rules.matchFields.map(f => {
        let val = getRowValue(row, f);
        // If this match field is an FK, resolve the integer ID to its display value
        // so it matches what the import file contains (e.g., Tag_ID: 42 -> "F1-80-FRA-SCC-157-TCV-1")
        const fkTable = fieldFkMap[tableConfig.dbTable + '.' + f];
        if (fkTable && fkIdToDisplayMap[fkTable]) {
          const resolved = fkIdToDisplayMap[fkTable].get(String(val));
          if (resolved) val = resolved;
        }
        return val;
      });
      const key = buildMatchKey(keyValues);
      if (key && key !== buildMatchKey(rules.matchFields.map(() => ''))) {
        index.set(key, row);
      }
    }
    matchIndexes[tableConfig.dbTable] = index;
  }

  // Pre-build dependency value sets (for fast dependency checking)
  // Supports both single field and multi-field: { field: 'X' } or { fields: ['X','Y'] }
  const depValueSets = {}; // "depTable" -> Set of lowercase values across all dep fields
  for (const [, rules] of Object.entries(TAB_RULES)) {
    if (!rules.dependsOn) continue;
    const depTable = rules.dependsOn.table;
    if (depValueSets[depTable]) continue;

    const depFields = rules.dependsOn.fields || [rules.dependsOn.field];
    const depRows = existingDataMap[depTable] || [];
    const values = new Set();
    for (const row of depRows) {
      for (const f of depFields) {
        const val = getRowValue(row, f);
        if (val != null && String(val).trim() !== '') {
          values.add(String(val).trim().toLowerCase());
        }
      }
    }
    depValueSets[depTable] = values;
  }

  // Build DB value sets for IMPORT_SOURCE_MAP targets
  // When a lookup category (e.g., Parent_Unique) is a filtered subset, we also need to
  // check the full source table in the DB (e.g., dbo.Panels.Panel)
  const dbSourceSets = {}; // "Table.Field" -> Set of lowercase values
  for (const [, source] of Object.entries(IMPORT_SOURCE_MAP)) {
    const setKey = source.table + '.' + source.field;
    if (dbSourceSets[setKey]) continue;
    const rows = existingDataMap[source.table] || [];
    const values = new Set();
    for (const row of rows) {
      const val = getRowValue(row, source.field);
      if (val != null && String(val).trim() !== '') {
        values.add(String(val).trim().toLowerCase());
      }
    }
    dbSourceSets[setKey] = values;
  }

  // Step 6: Validate each tab
  progress('Validating data...');
  const errorsByTab = {};
  const recordsByTab = {}; // insert/update/skip per tab
  let totalErrors = 0;
  let totalInserts = 0;
  let totalUpdates = 0;
  let totalSkips = 0;

  // Track values being imported on earlier tabs (for cross-tab dependency resolution)
  // e.g., new Drawings on tab 2 should satisfy Drawing_Log dependency on tab 3
  const importedValueSets = {}; // "tableName.fieldName" -> Set of lowercase values

  for (const tableConfig of TABLE_CONFIG) {
    const ws = workbook.getWorksheet(tableConfig.tabName);
    if (!ws) continue;

    const fields = metadataMap[tableConfig.dbTable];
    if (!fields) continue;

    const rules = TAB_RULES[tableConfig.dbTable];
    const tabErrors = [];
    const tabRecords = []; // insert/update/skip entries
    const seenKeys = new Map(); // matchKey -> first row number (detect duplicates within import)

    // Read header row to map columns
    const headerRow = ws.getRow(1);
    const colMap = {}; // fieldName -> colNumber
    headerRow.eachCell((cell, colNumber) => {
      const header = String(cell.value || '').trim();
      const field = fields.find(f =>
        f.display_Label === header ||
        f.field_Name === header ||
        (f.display_Label || f.field_Name.replace(/_/g, ' ')) === header
      );
      if (field) {
        colMap[field.field_Name] = colNumber;
      }
    });

    // Validate data rows
    const lastRow = ws.rowCount;
    for (let rowNum = 2; rowNum <= lastRow; rowNum++) {
      const row = ws.getRow(rowNum);

      // Skip completely empty rows
      let hasData = false;
      row.eachCell(() => { hasData = true; });
      if (!hasData) continue;

      // Build a plain object of this row's values for comparison
      const rowValues = {};

      // Always capture ID for ID-first matching, even though it's excluded from validation
      if (colMap['ID']) {
        const { value: idRaw } = unwrapCellValue(ws.getCell(rowNum, colMap['ID']).value);
        if (idRaw != null && String(idRaw).trim() !== '') {
          rowValues['ID'] = String(idRaw).trim();
        }
      }

      for (const field of fields) {
        const colNumber = colMap[field.field_Name];
        if (!colNumber) continue;
        if (EXCLUDE_FIELDS.includes(field.field_Name) || field.is_Computed) continue;
        const cell = ws.getCell(rowNum, colNumber);
        const { value: rawValue, error: unwrapError } = unwrapCellValue(cell.value);

        // If the cell contained a formula/error we couldn't resolve, flag it and skip storing garbage
        if (unwrapError) {
          tabErrors.push({
            row: rowNum, col: colNumber, field: field.field_Name,
            value: '',
            error: unwrapError
          });
          rowValues[field.field_Name] = '';
          continue;
        }

        // Convert Date objects to proper format strings for downstream use
        if (rawValue instanceof Date) {
          const fieldName = field.field_Name;
          if (fieldName.toLowerCase().includes('time') && field.uI_Type === 'text') {
            // Time field — format as "HH:MM:SS AM/PM"
            const h = rawValue.getHours();
            const m = String(rawValue.getMinutes()).padStart(2, '0');
            const s = String(rawValue.getSeconds()).padStart(2, '0');
            const ampm = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 || 12;
            rowValues[fieldName] = `${h12}:${m}:${s} ${ampm}`;
          } else {
            // Date field — format as ISO date string
            rowValues[fieldName] = rawValue.toISOString();
          }
        } else {
          rowValues[field.field_Name] = rawValue != null ? String(rawValue).trim() : '';
        }
      }

      // --- Field-level validation ---
      for (const field of fields) {
        const colNumber = colMap[field.field_Name];
        if (!colNumber) continue;
        if (EXCLUDE_FIELDS.includes(field.field_Name) || field.is_Computed) continue;

        const cell = ws.getCell(rowNum, colNumber);
        const { value: rawValue } = unwrapCellValue(cell.value);
        const strValue = rawValue != null ? String(rawValue).trim() : '';
        const isEmpty = strValue === '' || strValue === 'null' || strValue === 'undefined';
        const fieldName = field.field_Name;

        if (NO_VALIDATION.includes(fieldName)) continue;

        // Skip FK validation if this field is the current tab's own match key
        // e.g., Panel on Panels tab = new record identity, not a reference to another table
        const currentMatchFields = rules?.matchFields || [];
        if (currentMatchFields.includes(fieldName)) continue;

        // FK validation — check DB values, source table, and import tabs
        if (field.fK_Table && fkValuesMap[field.fK_Table] && !isEmpty) {
          const inDb = fkValuesMap[field.fK_Table].has(strValue.toLowerCase());
          // Check import values — try direct FK table key first, then alias mapping
          const directKey = field.fK_Table + '.' + (field.fK_Display_Field || field.field_Name);
          let inImport = importedValueSets[directKey]?.has(strValue.toLowerCase());
          let inSource = false;
          // If FK table is a view/alias (e.g., Parent_Unique), check the source table
          if (!inDb && !inImport && IMPORT_SOURCE_MAP[field.fK_Table]) {
            const source = IMPORT_SOURCE_MAP[field.fK_Table];
            const sourceKey = source.table + '.' + source.field;
            inSource = dbSourceSets[sourceKey]?.has(strValue.toLowerCase()) || false;
            inImport = importedValueSets[sourceKey]?.has(strValue.toLowerCase()) || false;
          }
          if (!inDb && !inSource && !inImport) {
            tabErrors.push({
              row: rowNum, col: colNumber, field: fieldName,
              value: strValue,
              error: `"${strValue}" not found in ${field.fK_Table} table`
            });
          }
        }
        // Lookup validation — check lookup values, DB source table, and import tabs
        if (field.lookup_Category && lookupSets[field.lookup_Category] && !isEmpty) {
          const inLookup = lookupSets[field.lookup_Category].has(strValue.toLowerCase());
          let inSource = false;
          let inImport = false;
          if (!inLookup && IMPORT_SOURCE_MAP[field.lookup_Category]) {
            const source = IMPORT_SOURCE_MAP[field.lookup_Category];
            const sourceKey = source.table + '.' + source.field;
            // Check the full DB source table (e.g., dbo.Panels.Parent — not just the filtered lookup)
            inSource = dbSourceSets[sourceKey]?.has(strValue.toLowerCase()) || false;
            // Check values from earlier import tabs (e.g., new Panels on the Panels tab)
            inImport = importedValueSets[sourceKey]?.has(strValue.toLowerCase()) || false;
          }
          if (!inLookup && !inSource && !inImport) {
            tabErrors.push({
              row: rowNum, col: colNumber, field: fieldName,
              value: strValue,
              error: `"${strValue}" not in ${field.lookup_Category} list`
            });
          }
        }

        // Date validation
        if (field.uI_Type === 'date' && !isEmpty) {
          const dateVal = rawValue instanceof Date ? rawValue : new Date(rawValue);
          if (isNaN(dateVal.getTime())) {
            tabErrors.push({
              row: rowNum, col: colNumber, field: fieldName,
              value: strValue, error: 'Invalid date format'
            });
          }
        }

        // Number validation
        if (field.uI_Type === 'number' && !isEmpty) {
          if (isNaN(Number(rawValue))) {
            tabErrors.push({
              row: rowNum, col: colNumber, field: fieldName,
              value: strValue, error: 'Expected a number'
            });
          }
        }

        // Time validation — accept HH:MM AM/PM or HH:MM:SS AM/PM
        // ExcelJS may return a Date object for time-formatted cells — extract time from it
        if (fieldName.toLowerCase().includes('time') && field.uI_Type === 'text' && !isEmpty) {
          let timeStr = strValue;
          if (rawValue instanceof Date) {
            const h = rawValue.getHours();
            const m = String(rawValue.getMinutes()).padStart(2, '0');
            const s = String(rawValue.getSeconds()).padStart(2, '0');
            const ampm = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 || 12;
            timeStr = `${h12}:${m}:${s} ${ampm}`;
          }
          const timeRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9](:[0-5][0-9])?\s*(AM|PM)$/i;
          if (!timeRegex.test(timeStr)) {
            tabErrors.push({
              row: rowNum, col: colNumber, field: fieldName,
              value: timeStr, error: 'Expected time format: HH:MM AM/PM'
            });
          }
        }

      }

      // --- Dependency validation ---
      // Check DB first, then also check values being imported on earlier tabs
      if (rules?.dependsOn) {
        const dep = rules.dependsOn;
        const depValue = rowValues[dep.importField] || '';
        if (depValue && depValue !== 'null' && depValue !== 'undefined') {
          const depSet = depValueSets[dep.table];
          const inDb = depSet && depSet.has(depValue.toLowerCase());
          // Check import tabs — try each dep field key
          const depFields = dep.fields || [dep.field];
          let inImport = false;
          for (const f of depFields) {
            const importKey = dep.table + '.' + f;
            if (importedValueSets[importKey]?.has(depValue.toLowerCase())) {
              inImport = true;
              break;
            }
          }
          if (!inDb && !inImport) {
            const depCol = colMap[dep.importField];
            tabErrors.push({
              row: rowNum,
              col: depCol || 1,
              field: dep.importField,
              value: depValue,
              error: `"${depValue}" not found in ${dep.table} table — must exist before import`
            });
          }
        }
      }

      // --- Record-level detection: insert / update / skip ---
      if (rules?.matchFields) {
        const matchIndex = matchIndexes[tableConfig.dbTable];
        if (matchIndex) {
          const keyValues = rules.matchFields.map(f => rowValues[f] || '');
          const matchKey = buildMatchKey(keyValues);
          const emptyKey = buildMatchKey(rules.matchFields.map(() => ''));

          // ID-first matching: if the row has an ID, find the existing record directly by ID
          const rowId = rowValues['ID'] ?? rowValues['id'];
          const existingDataRows = existingDataMap[tableConfig.dbTable] || [];
          const idMatchedRow = rowId
            ? existingDataRows.find(r => String(getRowValue(r, 'ID') ?? getRowValue(r, 'id')) === String(rowId).trim())
            : null;

          if (matchKey !== emptyKey || idMatchedRow) {
            // Check for duplicate within the import file (use matchKey or ID as dedup key)
            const matchDisplay = idMatchedRow
              ? `ID ${rowId}`
              : rules.matchFields.map(f => rowValues[f] || '').join(' / ');
            const dedupKey = idMatchedRow ? `__id__${rowId}` : matchKey;

            if (seenKeys.has(dedupKey) && !rules.allowDuplicates) {
              const firstRow = seenKeys.get(dedupKey);
              tabErrors.push({
                row: rowNum,
                col: colMap[rules.matchFields[0]] || 1,
                field: idMatchedRow ? 'ID' : rules.matchFields.join(', '),
                value: matchDisplay,
                error: `Duplicate record "${matchDisplay}" — same key as row ${firstRow}`
              });
            } else {
              if (!seenKeys.has(dedupKey)) seenKeys.set(dedupKey, rowNum);

              // ID match takes priority; fall back to matchFields index lookup
              const existingRow = idMatchedRow || matchIndex.get(matchKey);

              if (!existingRow) {
              // New record — will INSERT
              tabRecords.push({
                row: rowNum,
                status: 'insert',
                matchKey: matchDisplay,
                message: `New record "${matchDisplay}" — will insert`,
                changedFields: [],
                rowValues,
                existingId: null
              });
            } else {
              // Compare all columns (excluding system fields)
              const changedFields = [];
              for (const field of fields) {
                if (COMPARE_EXCLUDE.includes(field.field_Name) || field.is_Computed) continue;
                if (!colMap[field.field_Name]) continue;

                const importVal = rowValues[field.field_Name] || '';
                let existingVal = getRowValue(existingRow, field.field_Name);
                let existingStr = existingVal != null ? String(existingVal).trim() : '';

                // If this field is an FK, the DB stores the ID — resolve to display value
                const fkTable = fieldFkMap[tableConfig.dbTable + '.' + field.field_Name];
                if (fkTable && fkIdToDisplayMap[fkTable] && existingStr !== '') {
                  const resolved = fkIdToDisplayMap[fkTable].get(existingStr);
                  if (resolved) existingStr = resolved;
                }

                // Normalize for comparison (case-insensitive, handle dates)
                const normImport = importVal.toLowerCase();
                const normExisting = existingStr.toLowerCase();

                if (normImport !== normExisting && !(normImport === '' && normExisting === '')) {
                  changedFields.push({
                    field: field.field_Name,
                    col: colMap[field.field_Name],
                    oldValue: existingStr,
                    newValue: importVal
                  });
                }
              }

              const existingId = getRowValue(existingRow, 'ID') ?? getRowValue(existingRow, 'id');

              if (changedFields.length === 0) {
                tabRecords.push({
                  row: rowNum,
                  status: 'skip',
                  matchKey: matchDisplay,
                  message: `Record "${matchDisplay}" already exists — no changes`,
                  changedFields: [],
                  rowValues,
                  existingId
                });
              } else {
                const fieldNames = changedFields.map(c => c.field).join(', ');
                tabRecords.push({
                  row: rowNum,
                  status: 'update',
                  matchKey: matchDisplay,
                  message: `Record "${matchDisplay}" exists — ${changedFields.length} field${changedFields.length !== 1 ? 's' : ''} differ: ${fieldNames}`,
                  changedFields,
                  rowValues,
                  existingId
                });
              }
            }
            } // end else (not duplicate)
          }
        }
      }
    }

    // Collect values from this tab's import data so later tabs can reference them
    // e.g., new Drawings added on tab 2 should satisfy Drawing_Log's dependency on tab 3
    // Collect: match fields + any fields referenced by IMPORT_SOURCE_MAP
    const fieldsToCollect = new Set(rules?.matchFields || []);
    for (const [, source] of Object.entries(IMPORT_SOURCE_MAP)) {
      if (source.table === tableConfig.dbTable) {
        fieldsToCollect.add(source.field);
      }
    }

    for (const fieldToCollect of fieldsToCollect) {
      const setKey = tableConfig.dbTable + '.' + fieldToCollect;
      if (!importedValueSets[setKey]) importedValueSets[setKey] = new Set();
      const lastDataRow = ws.rowCount;
      for (let r = 2; r <= lastDataRow; r++) {
        const colNum = colMap[fieldToCollect];
        if (!colNum) continue;
        const { value: cellVal } = unwrapCellValue(ws.getCell(r, colNum).value);
        if (cellVal != null && String(cellVal).trim() !== '') {
          importedValueSets[setKey].add(String(cellVal).trim().toLowerCase());
        }
      }
    }

    if (tabErrors.length > 0) {
      errorsByTab[tableConfig.tabName] = tabErrors;
      totalErrors += tabErrors.length;
    }
    if (tabRecords.length > 0) {
      recordsByTab[tableConfig.tabName] = tabRecords;
      totalInserts += tabRecords.filter(r => r.status === 'insert').length;
      totalUpdates += tabRecords.filter(r => r.status === 'update').length;
      totalSkips += tabRecords.filter(r => r.status === 'skip').length;
    }
  }

  // Step 7: Build annotated workbook
  progress('Building validation report...');

  // Highlight error cells
  for (const [tabName, errors] of Object.entries(errorsByTab)) {
    const ws = workbook.getWorksheet(tabName);
    if (!ws) continue;
    for (const err of errors) {
      const cell = ws.getCell(err.row, err.col);
      cell.fill = ERROR_FILL;
      cell.note = err.error;
    }
  }

  // Highlight record action cells (insert/update/skip)
  for (const [tabName, records] of Object.entries(recordsByTab)) {
    const ws = workbook.getWorksheet(tabName);
    if (!ws) continue;

    const fields = metadataMap[TABLE_CONFIG.find(t => t.tabName === tabName)?.dbTable] || [];
    const headerRow = ws.getRow(1);
    const colMap = {};
    headerRow.eachCell((cell, colNumber) => {
      const header = String(cell.value || '').trim();
      const field = fields.find(f =>
        f.display_Label === header || f.field_Name === header ||
        (f.display_Label || f.field_Name.replace(/_/g, ' ')) === header
      );
      if (field) colMap[field.field_Name] = colNumber;
    });

    for (const rec of records) {
      const firstCol = Math.min(...Object.values(colMap));
      if (rec.status === 'insert') {
        // Green highlight on first cell
        ws.getCell(rec.row, firstCol).fill = INSERT_FILL;
        ws.getCell(rec.row, firstCol).note = rec.message;
      } else if (rec.status === 'skip') {
        // Gray out entire row
        for (const [, colNum] of Object.entries(colMap)) {
          ws.getCell(rec.row, colNum).fill = SKIP_FILL;
        }
        ws.getCell(rec.row, firstCol).note = rec.message;
      } else if (rec.status === 'update') {
        // Orange highlight on changed cells with old vs new comment
        for (const changed of rec.changedFields) {
          const cell = ws.getCell(rec.row, changed.col);
          cell.fill = WARNING_FILL;
          cell.note = `Will update: "${changed.oldValue}" → "${changed.newValue}"`;
        }
      }
    }
  }

  // Add/replace Validation Summary sheet
  const existingSummary = workbook.getWorksheet('Validation');
  if (existingSummary) workbook.removeWorksheet(existingSummary.id);

  const totalRecords = totalInserts + totalUpdates + totalSkips;
  const summary = workbook.addWorksheet('Validation', {
    properties: { tabColor: { argb: totalErrors > 0 ? 'FFC62828' : 'FF2E7D32' } }
  });
  summary.orderNo = 0;

  summary.columns = [
    { width: 5 },
    { width: 25 },
    { width: 10 },
    { width: 25 },
    { width: 30 },
    { width: 50 }
  ];

  // Title
  summary.mergeCells('B2:F2');
  const titleCell = summary.getCell('B2');
  const recordParts = [];
  if (totalInserts > 0) recordParts.push(`${totalInserts} insert${totalInserts !== 1 ? 's' : ''}`);
  if (totalUpdates > 0) recordParts.push(`${totalUpdates} update${totalUpdates !== 1 ? 's' : ''}`);
  if (totalSkips > 0) recordParts.push(`${totalSkips} skip${totalSkips !== 1 ? 's' : ''}`);
  const recordSummary = recordParts.length > 0 ? recordParts.join(', ') : '';

  if (totalErrors > 0) {
    titleCell.value = `Validation Failed — ${totalErrors} error${totalErrors !== 1 ? 's' : ''}${recordSummary ? ` | ${recordSummary}` : ''}`;
    titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FFC62828' } };
  } else if (totalRecords > 0) {
    titleCell.value = `Validation Passed — ${recordSummary}`;
    titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF2E7D32' } };
  } else {
    titleCell.value = 'Validation Passed — No records to process';
    titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF2E7D32' } };
  }

  summary.getCell('B3').value = `Validated: ${new Date().toLocaleString()}`;
  summary.getCell('B3').font = { name: 'Calibri', size: 11, color: { argb: 'FF64748B' } };

  let row = 5;

  // --- Errors Section ---
  if (totalErrors > 0) {
    summary.mergeCells(`B${row}:C${row}`);
    summary.getCell(`B${row}`).value = 'Errors by Tab';
    summary.getCell(`B${row}`).font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFC62828' } };
    row += 2;

    for (const [tabName, errors] of Object.entries(errorsByTab)) {
      summary.getCell(`B${row}`).value = tabName;
      summary.getCell(`B${row}`).font = { name: 'Calibri', size: 11, bold: true };
      summary.getCell(`C${row}`).value = `${errors.length} error${errors.length !== 1 ? 's' : ''}`;
      summary.getCell(`C${row}`).font = { name: 'Calibri', size: 11 };
      row++;
    }

    row += 1;
    summary.mergeCells(`B${row}:F${row}`);
    summary.getCell(`B${row}`).value = 'Error Details';
    summary.getCell(`B${row}`).font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFC62828' } };
    row += 2;

    const headers = ['Tab', 'Row', 'Field', 'Value', 'Error'];
    const headerCols = [2, 3, 4, 5, 6];
    headers.forEach((h, i) => {
      const cell = summary.getCell(row, headerCols[i]);
      cell.value = h;
      cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = SUMMARY_HEADER_FILL;
    });
    row++;

    for (const [tabName, errors] of Object.entries(errorsByTab)) {
      for (const err of errors) {
        summary.getCell(row, 2).value = tabName;
        summary.getCell(row, 3).value = err.row;
        summary.getCell(row, 4).value = err.field;
        summary.getCell(row, 5).value = err.value;
        summary.getCell(row, 6).value = err.error;
        for (let c = 2; c <= 6; c++) {
          summary.getCell(row, c).font = { name: 'Calibri', size: 11 };
        }
        row++;
      }
    }
    row += 1;
  }

  // --- Record Actions Section ---
  if (totalRecords > 0) {
    summary.mergeCells(`B${row}:C${row}`);
    summary.getCell(`B${row}`).value = 'Record Actions by Tab';
    summary.getCell(`B${row}`).font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FF1A5568' } };
    row += 2;

    for (const [tabName, records] of Object.entries(recordsByTab)) {
      const insertCount = records.filter(r => r.status === 'insert').length;
      const updateCount = records.filter(r => r.status === 'update').length;
      const skipCount = records.filter(r => r.status === 'skip').length;
      summary.getCell(`B${row}`).value = tabName;
      summary.getCell(`B${row}`).font = { name: 'Calibri', size: 11, bold: true };
      const parts = [];
      if (insertCount > 0) parts.push(`${insertCount} insert`);
      if (updateCount > 0) parts.push(`${updateCount} update`);
      if (skipCount > 0) parts.push(`${skipCount} skip`);
      summary.getCell(`C${row}`).value = parts.join(', ');
      summary.getCell(`C${row}`).font = { name: 'Calibri', size: 11 };
      row++;
    }

    row += 1;
    summary.mergeCells(`B${row}:F${row}`);
    summary.getCell(`B${row}`).value = 'Record Details';
    summary.getCell(`B${row}`).font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FF1A5568' } };
    row += 2;

    const rHeaders = ['Tab', 'Row', 'Action', 'Record', 'Details'];
    const rHeaderCols = [2, 3, 4, 5, 6];
    rHeaders.forEach((h, i) => {
      const cell = summary.getCell(row, rHeaderCols[i]);
      cell.value = h;
      cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = RECORD_HEADER_FILL;
    });
    row++;

    for (const [tabName, records] of Object.entries(recordsByTab)) {
      for (const rec of records) {
        summary.getCell(row, 2).value = tabName;
        summary.getCell(row, 3).value = rec.row;
        summary.getCell(row, 4).value = rec.status === 'insert' ? 'Insert' : rec.status === 'update' ? 'Update' : 'Skip';
        summary.getCell(row, 5).value = rec.matchKey;
        summary.getCell(row, 6).value = rec.status === 'insert'
          ? 'New record'
          : rec.status === 'skip'
            ? 'Already exists, no changes'
            : `Fields differ: ${rec.changedFields.map(c => c.field).join(', ')}`;
        for (let c = 2; c <= 6; c++) {
          summary.getCell(row, c).font = { name: 'Calibri', size: 11 };
        }
        row++;
      }
    }
  }

  const annotatedBuffer = await workbook.xlsx.writeBuffer();

  return {
    totalErrors,
    totalInserts,
    totalUpdates,
    totalSkips,
    errorsByTab,
    recordsByTab,
    annotatedBuffer,
    // Import engine data: FK resolution maps and metadata
    fkIdToDisplayMap,
    fieldFkMap,
    metadataMap
  };
}
